import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { walkiesClient } from "../api/walkiesClient";

type LoadState = "idle" | "loading" | "error";

function normalizeReturnUrl(raw?: string | null): string {
  if (!raw) return "/walker/dashboard";

  const trimmed = raw.trim();
  if (!trimmed) return "/walker/dashboard";
  if (!trimmed.startsWith("/")) return "/walker/dashboard";
  if (trimmed.startsWith("//")) return "/walker/dashboard";

  return trimmed;
}

async function delay(ms: number): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, ms));
}

function getConsumeKey(token?: string): string {
  return `wnm.walkerMagicConsume.${token ?? ""}`;
}

const WalkerMagicConsumePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();

  const returnUrl = useMemo(
    () => normalizeReturnUrl(searchParams.get("returnUrl")),
    [searchParams]
  );

  const [state, setState] = useState<LoadState>("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function run() {
      const trimmedToken = token?.trim();

      if (!trimmedToken) {
        if (!cancelled) {
          setState("error");
          setError("Magic access token is missing.");
        }
        return;
      }

      const consumeKey = getConsumeKey(trimmedToken);

      try {
        setState("loading");
        setError("");

        // Prevent duplicate consume attempts for the same token in this browser tab/session.
        const existingState = sessionStorage.getItem(consumeKey);

        if (existingState === "done") {
          window.location.replace(returnUrl);
          return;
        }

        if (existingState === "started") {
          // Another mount/run has already started consumption.
          // Give it a moment, then check session and continue.
          await delay(500);

          try {
            await walkiesClient.getWalkerMe();
            sessionStorage.setItem(consumeKey, "done");
            window.location.replace(returnUrl);
            return;
          } catch {
            // Fall through to error if session did not settle.
            throw new Error("Secure sign-in is already in progress. Please try the link again.");
          }
        }

        sessionStorage.setItem(consumeKey, "started");

        await walkiesClient.consumeWalkerMagicLink({
          token: trimmedToken,
        });

        await delay(400);
        await walkiesClient.getWalkerMe();

        sessionStorage.setItem(consumeKey, "done");
        window.location.replace(returnUrl);
      } catch (err) {
        sessionStorage.removeItem(getConsumeKey(trimmedToken));

        if (!cancelled) {
          setState("error");
          setError(
            err instanceof Error
              ? err.message
              : "Magic link is invalid, expired, or already used."
          );
        }
      }
    }

    void run();

    return () => {
      cancelled = true;
    };
  }, [token, returnUrl]);

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.card}>
          {state === "loading" || state === "idle" ? (
            <>
              <div style={styles.badge}>Secure access</div>
              <h1 style={styles.title}>Opening your walker dashboard…</h1>
              <p style={styles.text}>Please wait while we sign you in securely.</p>
            </>
          ) : (
            <>
              <div style={styles.badge}>Secure access</div>
              <h1 style={styles.title}>This access link is not available</h1>
              <p style={styles.text}>
                {error || "Magic link is invalid, expired, or already used."}
              </p>

              <div style={styles.actions}>
                <Link
                  to={`/walker/access?returnUrl=${encodeURIComponent(returnUrl)}`}
                  style={styles.primaryLinkButton}
                >
                  Request new link
                </Link>

                <Link to="/" style={styles.secondaryLinkButton}>
                  Back to home
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    background:
      "radial-gradient(circle at top right, rgba(242, 138, 22, 0.16), transparent 28%), linear-gradient(180deg, #2f4f46 0%, #213a33 100%)",
    padding: "32px 16px 64px",
    color: "#ffffff",
  },
  container: {
    maxWidth: 760,
    margin: "0 auto",
  },
  card: {
    background: "#f5f0e8",
    color: "#18201d",
    borderRadius: 28,
    padding: 28,
    boxShadow: "0 18px 48px rgba(0, 0, 0, 0.18)",
  },
  badge: {
    display: "inline-flex",
    padding: "8px 12px",
    borderRadius: 999,
    background: "rgba(47, 79, 70, 0.08)",
    fontWeight: 800,
    marginBottom: 16,
    color: "#213a33",
  },
  title: {
    margin: "0 0 12px",
    fontSize: 38,
    lineHeight: 1.02,
    letterSpacing: "-0.04em",
    fontWeight: 900,
    color: "#213a33",
  },
  text: {
    margin: 0,
    lineHeight: 1.7,
    color: "rgba(24, 32, 29, 0.8)",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  actions: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    marginTop: 20,
  },
  primaryLinkButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 16,
    padding: "14px 18px",
    fontSize: 15,
    fontWeight: 800,
    background: "#f28a16",
    color: "#ffffff",
    textDecoration: "none",
  },
  secondaryLinkButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 16,
    padding: "14px 18px",
    fontSize: 15,
    fontWeight: 800,
    background: "#213a33",
    color: "#ffffff",
    textDecoration: "none",
  },
};

export default WalkerMagicConsumePage;